import {Option} from 'platform/components';

import {head} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {useListRentalCalendarVehiclesQuery} from '@dms/api/vehicle';
import {useVehicleCatalogue, useVehicleMake, useVehicleModel} from '@dms/shared';

import {CAR_PARK_CODELIST_ID} from '../constants/carParkCodelistId';
import {VEHICLE_CATEGORY_CODELIST_ID} from '../constants/vehicleCategoryCodelistId';
import type {RentalCalendarFilterValues} from '../types/RentalCalendarFilterValues';

type OptionGroup = {label: string; options: readonly Option[]};
export type RentalCalendarFilterOptions = readonly (Option | OptionGroup)[];

/**
 * Catalogue options for every calendar filter. Shared by the filter panel and the active-filter
 * chips so both resolve a selected value to the same label; RTK caches the underlying queries.
 */
export function useRentalCalendarFilterOptions(filters: RentalCalendarFilterValues) {
  // ponytail: catalogue enums are vehicle-type scoped; scope by the first selected type,
  // fall back to passenger cars. Per-type option merging only if users ask for it.
  const catalogueVehicleType = head(filters.vehicleType) ?? DEFAULT_VEHICLE_TYPE;

  const [vehicleUtils, {isLoading: isCatalogueLoading}] = useVehicleCatalogue(catalogueVehicleType);
  const [{groupedMakeOptionsWithTopMakes}, {isLoading: areMakesLoading}] =
    useVehicleMake(catalogueVehicleType);
  const [{groupedModelOptionsByMake}, {isLoading: areModelsLoading}] = useVehicleModel(
    catalogueVehicleType,
    filters.make
  );

  const {data: allVehicles, isLoading: areVehiclesLoading} = useListRentalCalendarVehiclesQuery({});

  const {data: categoryData, isLoading: areCategoriesLoading} = useReadCodelistQuery({
    codelistId: VEHICLE_CATEGORY_CODELIST_ID,
  });

  const {data: carParkData, isLoading: areCarParksLoading} = useReadCodelistQuery({
    codelistId: CAR_PARK_CODELIST_ID,
  });

  const options = {
    vehicleId:
      allVehicles?.map((vehicle) => ({
        value: vehicle.vehicleId,
        label: isNotNilOrEmpty(vehicle.registrationPlate)
          ? `${vehicle.title} · ${vehicle.registrationPlate}`
          : vehicle.title,
      })) ?? [],
    vehicleCategory:
      categoryData?.codes?.map((code) => ({value: code.codeId, label: code.name})) ?? [],
    carPark: carParkData?.codes?.map((code) => ({value: code.codeId, label: code.name})) ?? [],
    vehicleType: vehicleUtils.vehicleTypeOptions ?? [],
    make: groupedMakeOptionsWithTopMakes ?? [],
    modelFamily: groupedModelOptionsByMake ?? [],
    bodyStyle: vehicleUtils.vehicleStyleOptions ?? [],
    fuelType: vehicleUtils.fuelTypeOptions ?? [],
    transmission: vehicleUtils.transmissionOptions ?? [],
    drive: vehicleUtils.driveOptions ?? [],
    doorCount: vehicleUtils.doorCountOptions ?? [],
    seatCount: vehicleUtils.seatCountOptions ?? [],
  } satisfies Record<keyof RentalCalendarFilterValues, RentalCalendarFilterOptions>;

  return {
    options,
    // Selected values are ids. Until every source resolves there is no label to match them
    // against, and a chip would show the raw uuid
    isLoading:
      isCatalogueLoading ||
      areMakesLoading ||
      areModelsLoading ||
      areVehiclesLoading ||
      areCategoriesLoading ||
      areCarParksLoading,
  };
}

const DEFAULT_VEHICLE_TYPE = 'VEHICLETYPE_PASSENGER_CAR';
